import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { contentPadding } from '@monorepo/shared/styles/layout';
import { bodyRegular, bodySmall } from '@monorepo/shared/styles/text';
import React from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { Button } from './Button/Button';

const DropArea = styled.div<{ $isDragActive: boolean }>`
  ${contentPadding}
  ${bodyRegular}
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  margin: 0;
  color: ${({ theme }) => theme.colors.gray666};
  background-color: ${({ $isDragActive, theme }) =>
    $isDragActive ? theme.colors.builderBlue08 : theme.colors.white};
  border: 1px dashed
    ${({ $isDragActive, theme }) =>
      $isDragActive ? theme.colors.builderBlue : theme.colors.grayddd};
  border-radius: 4px;
`;

const Footer = styled.div`
  ${bodySmall}
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 0.25rem;
  color: ${({ theme }) => theme.colors.gray666};
`;

const FooterRight = styled.div`
  text-align: right;
`;

type FileDropzoneProps = {
  inputLabel: string;
  buttonText: string;
  acceptedMimeTypes: string[];
  onFilesSelected: (files: File[]) => void;
  supportedFileTypeLabels?: string[];
  fileSizeLimitInMb: number;
};

export function FileDropzone({
  inputLabel,
  buttonText,
  acceptedMimeTypes,
  onFilesSelected,
  supportedFileTypeLabels,
  fileSizeLimitInMb,
}: FileDropzoneProps) {
  const { error: showErrorToast } = useToast();

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: Object.fromEntries(
      acceptedMimeTypes.map((mimeType) => [mimeType, []]),
    ),
    maxSize: fileSizeLimitInMb * 1024 * 1024,

    noClick: true, // selecting a file is reserved for our custom UploadFiles button
    noKeyboard: true,
    onDropAccepted: (acceptedFiles) => onFilesSelected(acceptedFiles),
    onDropRejected: (fileRejections) => {
      const countRejections = (errorCode: ErrorCode) =>
        fileRejections.filter(({ errors }) =>
          errors.some(({ code }) => code === errorCode),
        ).length;

      const invalidTypeCount = countRejections(ErrorCode.FileInvalidType);
      const tooLargeCount = countRejections(ErrorCode.FileTooLarge);
      showErrorToast(
        [
          invalidTypeCount > 0 &&
            `${invalidTypeCount} file(s) are not a supported file type.`,
          tooLargeCount > 0 &&
            `${tooLargeCount} file(s) are too large. Please ensure all files are smaller than ${fileSizeLimitInMb}MB.`,
        ]
          .filter(Boolean)
          .join(' '),
        { dontAutoHide: true },
      );
    },
  });

  return (
    <>
      <DropArea {...getRootProps()} $isDragActive={isDragActive}>
        <input {...getInputProps()} aria-hidden />
        {inputLabel}
        <Button variant="outlined" color="primary" onClick={open}>
          {buttonText}
        </Button>
      </DropArea>
      {(supportedFileTypeLabels || !!fileSizeLimitInMb) && (
        <Footer>
          {supportedFileTypeLabels && (
            <div>Supported formats: {supportedFileTypeLabels.join(', ')}</div>
          )}
          {!!fileSizeLimitInMb && (
            <FooterRight>
              Maximum size per file: {fileSizeLimitInMb}MB
            </FooterRight>
          )}
        </Footer>
      )}
    </>
  );
}
